import React, { useState, useEffect } from 'react';
import { getAuthToken } from '../../utils/authToken';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  CalendarIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  UserIcon,
  DocumentTextIcon,
  TagIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  ShareIcon,
  HeartIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import ApplicationForm from '../../components/CallForProjects/ApplicationForm';
import ShareCallModal from '../../components/CallForProjects/ShareCallModal';
import { buildShareText, sharePageUrl, posterUrl } from '../../utils/shareCallForProject';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';

const CallForProjectDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasApplied, setHasApplied] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [shareText, setShareText] = useState(null);

  useEffect(() => {
    fetchProject();
    if (user) {
      checkApplication();
    }
  }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProject = async () => {
    setLoading(true);
    try {
      const jeton = await getAuthToken();
      const response = await fetch(`/api/call-for-projects/${id}`, jeton ? { headers: { Authorization: `Bearer ${jeton}` } } : undefined);
      
      if (response.ok) {
        const data = await response.json();
        setProject(data);
        
        // Incrémenter le compteur de vues
        incrementViews();
      } else {
        toast.error('Appel à projet non trouvé');
        navigate('/call-for-projects');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const incrementViews = async () => {
    try {
      await fetch(`/api/call-for-projects/${id}/view`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Erreur incrémentation vues:', error);
    }
  };

  const checkApplication = async () => {
    try {
      const token = await getAuthToken();
      const response = await fetch(`/api/my-applications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const applications = await response.json();
        const userApplication = applications.find(app => app.projectId === parseInt(id));
        setHasApplied(!!userApplication);
      }
    } catch (error) {
      console.error('Erreur vérification candidature:', error);
    }
  };

  const handleApply = async (applicationData) => {
    if (!user) {
      toast.error('Vous devez être connecté pour candidater');
      return;
    }

    setApplicationLoading(true);
    try {
      const token = await getAuthToken();
      
      // Préparer les données pour l'envoi
      const formData = new FormData();
      formData.append('message', applicationData.message);
      formData.append('contactInfo', JSON.stringify(applicationData.contactInfo));
      formData.append('additionalInfo', JSON.stringify(applicationData.additionalInfo));
      
      // Ajouter les documents
      applicationData.documents.forEach((file, index) => {
        formData.append(`document_${index}`, file);
      });

      const response = await fetch(`/api/call-for-projects/${id}/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        toast.success('Candidature envoyée avec succès !');
        setShowApplicationModal(false);
        setHasApplied(true);
        // Rafraîchir les données du projet pour mettre à jour le compteur
        fetchProject();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Erreur lors de l\'envoi de la candidature');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    } finally {
      setApplicationLoading(false);
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'financement':
        return 'Financement';
      case 'formation':
        return 'Formation';
      case 'concours':
        return 'Concours';
      case 'partenariat':
        return 'Partenariat';
      case 'emploi':
        return 'Emploi';
      default:
        return type;
    }
  };

  const shareProject = () => {
    setShareText(buildShareText(project, sharePageUrl(project)));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Appel à projet non trouvé</h2>
          <Link to="/call-for-projects" className="text-primary-600 hover:text-primary-700">
            Retour à la liste
          </Link>
        </div>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining(project.deadline);
  const isUrgent = daysRemaining <= 7 && daysRemaining > 0;
  const isExpired = daysRemaining <= 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Bouton retour */}
        <div className="mb-6">
          <Link
            to="/call-for-projects"
            className="inline-flex items-center text-primary-600 hover:text-primary-700"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Retour à la liste
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* En-tête */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-8 py-6 text-white">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-white/20 text-white`}>
                    {getTypeLabel(project.type)}
                  </span>
                  <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-white/20 text-white">
                    {project.category}
                  </span>
                </div>
                <h1 className="text-3xl font-bold mb-2">{project.title}</h1>
                <div className="flex items-center gap-6 text-white/90">
                  <div className="flex items-center">
                    <UserIcon className="h-5 w-5 mr-2" />
                    <span>{project.owner?.firstName} {project.owner?.lastName}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPinIcon className="h-5 w-5 mr-2" />
                    <span>{project.location?.country || 'Non spécifié'}</span>
                  </div>
                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2" />
                    <span>Publié le {formatDate(project.createdAt)}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={shareProject}
                  className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                  title="Partager"
                  aria-label="Partager cet appel à projets"
                >
                  <ShareIcon className="h-5 w-5" />
                </button>
                <button className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors">
                  <HeartIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Informations clés */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <CurrencyDollarIcon className="h-6 w-6 text-green-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Budget</h3>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(project.budget, project.currency)}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <CalendarIcon className="h-6 w-6 text-blue-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Date limite</h3>
                </div>
                <p className={`text-2xl font-bold ${isExpired ? 'text-red-600' : isUrgent ? 'text-orange-600' : 'text-blue-600'}`}>
                  {formatDate(project.deadline)}
                </p>
                {!isExpired && (
                  <p className={`text-sm ${isUrgent ? 'text-orange-600' : 'text-gray-600'}`}>
                    {daysRemaining} jour{daysRemaining > 1 ? 's' : ''} restant{daysRemaining > 1 ? 's' : ''}
                  </p>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <DocumentTextIcon className="h-6 w-6 text-purple-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Candidatures</h3>
                </div>
                <p className="text-2xl font-bold text-purple-600">
                  {project.applicationsCount}
                </p>
                <p className="text-sm text-gray-600">
                  {project.viewsCount} vues
                </p>
              </div>
            </div>

            {project.reserve && (
              <div className="mb-8 rounded-lg border border-amber-200 bg-amber-50 p-5 text-amber-900">
                <p className="font-semibold">Appel réservé aux abonnés Pro et Pro Max</p>
                <p className="text-sm mt-1">La description, les conditions et la candidature sont ouvertes avec l'abonnement Pro ou Pro Max.</p>
                <a href="https://app.innovactive360.cloud/tarifs" className="inline-block mt-3 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold">Voir les abonnements</a>
              </div>
            )}

            {/* Description */}
            {!project.reserve && <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Description</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {project.description}
                </p>
              </div>
            </div>}

            {/* Exigences */}
            {project.requirements && project.requirements.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Exigences</h2>
                <ul className="space-y-2">
                  {project.requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Avantages */}
            {project.benefits && project.benefits.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Avantages</h2>
                <ul className="space-y-2">
                  {project.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tags */}
            {project.tags && project.tags.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Tags</h2>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full">
                      <TagIcon className="h-4 w-4 mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Contact */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                    <a href={`mailto:${project.contactEmail}`} className="text-primary-600 hover:text-primary-700">
                      {project.contactEmail}
                    </a>
                  </div>
                  {project.contactPhone && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Téléphone</h3>
                      <a href={`tel:${project.contactPhone}`} className="text-primary-600 hover:text-primary-700">
                        {project.contactPhone}
                      </a>
                    </div>
                  )}
                  {project.website && (
                    <div className="md:col-span-2">
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {project.receivesApplications ? 'Site web' : "Site de l'organisateur"}
                      </h3>
                      <a href={project.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">
                        {project.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              {!project.receivesApplications ? (
                // Appel lancé par un autre organisme : lui seul reçoit les candidatures.
                isExpired ? (
                  <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-red-600 mr-2" />
                      <span className="text-red-800 font-medium">
                        Les candidatures pour cet appel à projet sont fermées.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1">
                    {project.applicationUrl ? (
                      <a
                        href={project.applicationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex w-full items-center justify-center gap-2 bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                      >
                        Candidater sur le site de l'organisateur
                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                      </a>
                    ) : null}
                    <p className="mt-2 text-sm text-gray-600">
                      {project.applicationUrl
                        ? "Les candidatures sont reçues par l'organisateur de l'appel, pas par Innovactive360."
                        : "Les candidatures sont reçues par l'organisateur de l'appel : contactez-le avec les coordonnées ci-dessus."}
                    </p>
                  </div>
                )
              ) : !user ? (
                <div className="flex-1 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                    <span className="text-yellow-800">
                      Vous devez être connecté pour candidater à cet appel à projet.
                    </span>
                  </div>
                  <div className="mt-2">
                    <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                      Se connecter
                    </Link>
                    <span className="text-yellow-600 mx-2">ou</span>
                    <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
                      S'inscrire
                    </Link>
                  </div>
                </div>
              ) : hasApplied ? (
                <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                    <span className="text-green-800 font-medium">
                      Vous avez déjà candidaté à cet appel à projet.
                    </span>
                  </div>
                </div>
              ) : isExpired ? (
                <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <ClockIcon className="h-5 w-5 text-red-600 mr-2" />
                    <span className="text-red-800 font-medium">
                      Les candidatures pour cet appel à projet sont fermées.
                    </span>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowApplicationModal(true)}
                  className="flex-1 bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Candidater maintenant
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {shareText !== null && (
        <ShareCallModal
          initialText={shareText}
          title={project.title}
          shareUrl={sharePageUrl(project)}
          posterUrlFor={(format) => posterUrl(project, format)}
          onClose={() => setShareText(null)}
        />
      )}

      {/* Modal de candidature */}
      {showApplicationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Candidater à "{project.title}"
                </h3>
                <button
                  onClick={() => setShowApplicationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <ApplicationForm
                project={project}
                onSubmit={handleApply}
                onCancel={() => setShowApplicationModal(false)}
                loading={applicationLoading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallForProjectDetail;

